/**
 * Canonical BOP.INK Icon Registry & Semantic Resolver.
 * Resolves semantic iconKey to official BOP utility icon assets.
 * Derived from user-approved official BOP asset sheets.
 */

export interface BopIconDefinition {
  iconKey: string;
  assetPath: string;
  family: string;
  label: string;
}

export const BOP_OFFICIAL_ICONS: Record<string, BopIconDefinition> = {
  // Finance & Payroll
  salary_briefcase: {
    iconKey: 'salary_briefcase',
    assetPath: '/assets/icons/optimized/salary_briefcase.webp',
    family: 'finance',
    label: 'Tiền lương & Thu nhập'
  },
  document_check: {
    iconKey: 'document_check',
    assetPath: '/assets/icons/optimized/document_check.webp',
    family: 'finance',
    label: 'Thuế & Tờ khai'
  },
  calculator_finance: {
    iconKey: 'calculator_finance',
    assetPath: '/assets/icons/optimized/calculator_finance.webp',
    family: 'finance',
    label: 'Tính toán tài chính'
  },
  coins_currency: {
    iconKey: 'coins_currency',
    assetPath: '/assets/icons/optimized/coins_currency.webp',
    family: 'finance',
    label: 'Tiền tệ & Tài chính'
  },
  bank_finance: {
    iconKey: 'bank_finance',
    assetPath: '/assets/icons/optimized/bank_finance.webp',
    family: 'finance',
    label: 'Ngân hàng & Lãi suất'
  },
  currency_exchange: {
    iconKey: 'currency_exchange',
    assetPath: '/assets/icons/optimized/currency_exchange.webp',
    family: 'finance',
    label: 'Quy đổi ngoại tệ'
  },
  gold_bars: {
    iconKey: 'gold_bars',
    assetPath: '/assets/icons/optimized/gold_bars.webp',
    family: 'finance',
    label: 'Vàng & Kim loại quý'
  },

  // Measurement & Units
  measurement_ruler: {
    iconKey: 'measurement_ruler',
    assetPath: '/assets/icons/optimized/measurement_ruler.webp',
    family: 'measurement',
    label: 'Đo lường & Quy đổi'
  },

  // Security & Cryptography
  lock_hash: {
    iconKey: 'lock_hash',
    assetPath: '/assets/icons/optimized/lock_hash.webp',
    family: 'security',
    label: 'Mã băm & Bảo mật'
  },
  shield_verified: {
    iconKey: 'shield_verified',
    assetPath: '/assets/icons/optimized/shield_verified.webp',
    family: 'security',
    label: 'Bảo vệ & Xác thực'
  },

  // Media & Images
  image_compress: {
    iconKey: 'image_compress',
    assetPath: '/assets/icons/optimized/image_compress.webp',
    family: 'media',
    label: 'Nén & Tối ưu ảnh'
  },
  image_edit: {
    iconKey: 'image_edit',
    assetPath: '/assets/icons/optimized/image_edit.webp',
    family: 'media',
    label: 'Chỉnh sửa hình ảnh'
  },
  crop_frame: {
    iconKey: 'crop_frame',
    assetPath: '/assets/icons/optimized/crop_frame.webp',
    family: 'media',
    label: 'Cắt khung & Tỷ lệ'
  },
  qrcode_scan: {
    iconKey: 'qrcode_scan',
    assetPath: '/assets/icons/optimized/qrcode_scan.webp',
    family: 'media',
    label: 'Mã QR & Quét mã'
  },

  // Utilities & Energy
  lightning_electricity: {
    iconKey: 'lightning_electricity',
    assetPath: '/assets/icons/optimized/lightning_electricity.webp',
    family: 'utility',
    label: 'Tiền điện & Năng lượng'
  },
  water_droplet: {
    iconKey: 'water_droplet',
    assetPath: '/assets/icons/optimized/water_droplet.webp',
    family: 'utility',
    label: 'Tiền nước & Chất lỏng'
  },
  gas_fuel: {
    iconKey: 'gas_fuel',
    assetPath: '/assets/icons/optimized/gas_fuel.webp',
    family: 'utility',
    label: 'Nhiên liệu & Xăng dầu'
  },

  // Files & PDF
  folder_files: {
    iconKey: 'folder_files',
    assetPath: '/assets/icons/optimized/folder_files.webp',
    family: 'file',
    label: 'Quản lý tập tin'
  },
  folder_upload: {
    iconKey: 'folder_upload',
    assetPath: '/assets/icons/optimized/folder_upload.webp',
    family: 'file',
    label: 'Tải lên tập tin'
  },
  pdf_gear: {
    iconKey: 'pdf_gear',
    assetPath: '/assets/icons/optimized/pdf_gear.webp',
    family: 'file',
    label: 'Chuyển đổi PDF'
  },
  pdf_document: {
    iconKey: 'pdf_document',
    assetPath: '/assets/icons/optimized/pdf_document.webp',
    family: 'file',
    label: 'Tài liệu PDF'
  },
  document_export: {
    iconKey: 'document_export',
    assetPath: '/assets/icons/optimized/document_export.webp',
    family: 'file',
    label: 'Xuất văn bản'
  },
  document_edit: {
    iconKey: 'document_edit',
    assetPath: '/assets/icons/optimized/document_edit.webp',
    family: 'file',
    label: 'Soạn thảo văn bản'
  },
  document_pen: {
    iconKey: 'document_pen',
    assetPath: '/assets/icons/optimized/document_pen.webp',
    family: 'file',
    label: 'Ký tên & Biểu mẫu'
  },

  // Code & Developer
  code_monitor_check: {
    iconKey: 'code_monitor_check',
    assetPath: '/assets/icons/optimized/code_monitor_check.webp',
    family: 'code',
    label: 'Lập trình & Kiểm thử'
  },
  code_monitor_gear: {
    iconKey: 'code_monitor_gear',
    assetPath: '/assets/icons/optimized/code_monitor_gear.webp',
    family: 'code',
    label: 'Tiện ích lập trình viên'
  },
  share_nodes: {
    iconKey: 'share_nodes',
    assetPath: '/assets/icons/optimized/share_nodes.webp',
    family: 'code',
    label: 'Liên kết & Đồ thị'
  },

  // Legal & Education
  legal_scales: {
    iconKey: 'legal_scales',
    assetPath: '/assets/icons/optimized/legal_scales.webp',
    family: 'legal',
    label: 'Pháp luật & Hợp đồng'
  },
  education_cap: {
    iconKey: 'education_cap',
    assetPath: '/assets/icons/optimized/education_cap.webp',
    family: 'education',
    label: 'Giáo dục & Thi cử'
  },
  education_cap_alt: {
    iconKey: 'education_cap_alt',
    assetPath: '/assets/icons/optimized/education_cap_alt.webp',
    family: 'education',
    label: 'Đào tạo & Học tập'
  },

  // Health & Medical
  health_heartbeat: {
    iconKey: 'health_heartbeat',
    assetPath: '/assets/icons/optimized/health_heartbeat.webp',
    family: 'health',
    label: 'Y tế & Chỉ số sức khỏe'
  },
  health_heart: {
    iconKey: 'health_heart',
    assetPath: '/assets/icons/optimized/health_heart.webp',
    family: 'health',
    label: 'Thể lực & Sức khỏe'
  },

  // Business & Places
  storefront_business: {
    iconKey: 'storefront_business',
    assetPath: '/assets/icons/optimized/storefront_business.webp',
    family: 'business',
    label: 'Cửa hàng & Kinh doanh'
  },
  home_house: {
    iconKey: 'home_house',
    assetPath: '/assets/icons/optimized/home_house.webp',
    family: 'living',
    label: 'Nhà ở & Đời sống'
  },
  map_pin: {
    iconKey: 'map_pin',
    assetPath: '/assets/icons/optimized/map_pin.webp',
    family: 'location',
    label: 'Bản đồ & Địa chính'
  },

  // Time & Calendar
  clock_history: {
    iconKey: 'clock_history',
    assetPath: '/assets/icons/optimized/clock_history.webp',
    family: 'time',
    label: 'Thời gian & Đồng hồ'
  },
  calendar_check: {
    iconKey: 'calendar_check',
    assetPath: '/assets/icons/optimized/calendar_check.webp',
    family: 'time',
    label: 'Lịch & Ngày công'
  },
  calendar_water: {
    iconKey: 'calendar_water',
    assetPath: '/assets/icons/optimized/calendar_water.webp',
    family: 'time',
    label: 'Chu kỳ & Lịch âm'
  },

  // Audio & Communication
  audio_headphones: {
    iconKey: 'audio_headphones',
    assetPath: '/assets/icons/optimized/audio_headphones.webp',
    family: 'audio',
    label: 'Âm thanh & Tai nghe'
  },
  audio_music: {
    iconKey: 'audio_music',
    assetPath: '/assets/icons/optimized/audio_music.webp',
    family: 'audio',
    label: 'Nhạc & Tiếng ồn'
  },
  microphone_voice: {
    iconKey: 'microphone_voice',
    assetPath: '/assets/icons/optimized/microphone_voice.webp',
    family: 'audio',
    label: 'Giọng nói & Thu âm'
  },
  chat_speech: {
    iconKey: 'chat_speech',
    assetPath: '/assets/icons/optimized/chat_speech.webp',
    family: 'social',
    label: 'Hội thoại & Tin nhắn'
  },
  chat_dots: {
    iconKey: 'chat_dots',
    assetPath: '/assets/icons/optimized/chat_dots.webp',
    family: 'social',
    label: 'Nhắn tin & Trao đổi'
  },

  // Discovery & Interface
  search_lookup: {
    iconKey: 'search_lookup',
    assetPath: '/assets/icons/optimized/search_lookup.webp',
    family: 'ui',
    label: 'Tìm kiếm & Tra cứu'
  },
  bookmark_favorite: {
    iconKey: 'bookmark_favorite',
    assetPath: '/assets/icons/optimized/bookmark_favorite.webp',
    family: 'ui',
    label: 'Đánh dấu & Yêu thích'
  },
  grid_apps: {
    iconKey: 'grid_apps',
    assetPath: '/assets/icons/optimized/grid_apps.webp',
    family: 'ui',
    label: 'Danh mục công cụ'
  },
  sliders_settings: {
    iconKey: 'sliders_settings',
    assetPath: '/assets/icons/optimized/sliders_settings.webp',
    family: 'ui',
    label: 'Cài đặt & Bộ lọc'
  },
  lottery_balls: {
    iconKey: 'lottery_balls',
    assetPath: '/assets/icons/optimized/lottery_balls.webp',
    family: 'random',
    label: 'Xổ số & Ngẫu nhiên'
  },
  menu_hamburger: {
    iconKey: 'menu_hamburger',
    assetPath: '/assets/icons/optimized/menu_hamburger.webp',
    family: 'ui',
    label: 'Menu'
  },
  close_x: {
    iconKey: 'close_x',
    assetPath: '/assets/icons/optimized/close_x.webp',
    family: 'ui',
    label: 'Đóng'
  },
  arrow_right: {
    iconKey: 'arrow_right',
    assetPath: '/assets/icons/optimized/arrow_right.webp',
    family: 'navigation',
    label: 'Tiếp theo'
  },
  arrow_left: {
    iconKey: 'arrow_left',
    assetPath: '/assets/icons/optimized/arrow_left.webp',
    family: 'navigation',
    label: 'Quay lại'
  }
};

/**
 * Mapping table from legacy Lucide icon names and semantic aliases to official BOP icon keys.
 */
export const ICON_NAME_ALIAS_MAP: Record<string, string> = {
  Receipt: 'salary_briefcase',
  Calculator: 'calculator_finance',
  BadgePercent: 'calculator_finance',
  Scale: 'measurement_ruler',
  Ruler: 'measurement_ruler',
  Lock: 'lock_hash',
  KeyRound: 'lock_hash',
  Shield: 'shield_verified',
  ShieldAlert: 'shield_verified',
  Image: 'image_compress',
  Crop: 'crop_frame',
  Zap: 'lightning_electricity',
  FileText: 'document_check',
  Files: 'folder_files',
  FileSpreadsheet: 'document_export',
  FileCode: 'code_monitor_gear',
  QrCode: 'qrcode_scan',
  HeartPulse: 'health_heartbeat',
  Activity: 'health_heart',
  Flame: 'gas_fuel',
  Car: 'gas_fuel',
  Clock: 'clock_history',
  Calendar: 'calendar_check',
  Search: 'search_lookup',
  Share2: 'share_nodes',
  Code2: 'code_monitor_check',
  Braces: 'code_monitor_gear',
  Binary: 'code_monitor_gear',
  MessageSquare: 'chat_speech',
  Sliders: 'sliders_settings',
  SlidersHorizontal: 'sliders_settings',
  Minimize2: 'image_compress',
  Dices: 'lottery_balls',
  Link2: 'share_nodes',
  Languages: 'chat_dots',
  Pilcrow: 'document_edit',
  AlignLeft: 'document_edit',
  Sparkles: 'lightning_electricity',
  GraduationCap: 'education_cap',
  Headphones: 'audio_headphones',
  ShieldCheck: 'shield_verified',
  FileClock: 'clock_history',
  CalendarCheck: 'calendar_check',
  CalendarClock: 'clock_history',
  CalendarDays: 'calendar_check',
  PieChart: 'calculator_finance',
  ShoppingCart: 'storefront_business',
  Layers: 'folder_files',
  Grid: 'grid_apps',
  Split: 'sliders_settings',
  CopyCheck: 'document_check',
  Boxes: 'folder_files',
  Barcode: 'qrcode_scan',
  CupSoda: 'health_heart',
  Wine: 'health_heart',
  Sprout: 'home_house',
  Flower2: 'home_house',
  Blinds: 'home_house',
  Shirt: 'home_house',
  Cpu: 'code_monitor_gear',
  Video: 'image_compress',
  Filter: 'sliders_settings',
  BookOpen: 'education_cap',
  ArrowLeftRight: 'currency_exchange',
  CheckCircle2: 'document_check',
  RectangleHorizontal: 'crop_frame',
  ThermometerSnowflake: 'clock_history',
  Snowflake: 'clock_history'
};

/**
 * Resolves an icon name or semantic iconKey to an official BOP asset path if available.
 */
export function resolveBopIcon(iconNameOrKey?: string): BopIconDefinition | null {
  if (!iconNameOrKey) return null;

  if (BOP_OFFICIAL_ICONS[iconNameOrKey]) {
    return BOP_OFFICIAL_ICONS[iconNameOrKey];
  }

  const mappedKey = ICON_NAME_ALIAS_MAP[iconNameOrKey];
  if (mappedKey && BOP_OFFICIAL_ICONS[mappedKey]) {
    return BOP_OFFICIAL_ICONS[mappedKey];
  }

  return null;
}
